/**
 * The CSS-inspector hover tooltip, drawn the same way on every surface.
 *
 * The extension (React, pins/inspector-tooltip.tsx) and team review (the guest
 * bundle's 0220 section) each used to draw their own, and they drifted: the
 * extension said "size/weight 30px / 700", team review said "font-size", only
 * one had border-radius, only the other had line height and token badges
 * (Emily, 2026-10-22: "can we not merge them so we dont have to maintain two?").
 * Both now hand the same data shape to renderInspectorTooltip, the pattern the
 * capture toolbar set (captureToolbarDom.ts). Positioning stays per surface.
 *
 * Team review's values come from a third-party framed page and are untrusted:
 * everything is written with textContent, and a swatch is painted only for a
 * real hex color.
 *
 * Styles ship inside the container so a page without the extension's --oq-*
 * tokens renders it identically. Each value reads the extension token first and
 * falls back to the design-system constant (or, for the three accents the
 * shared palette does not carry, the token's own value in _tokens.css).
 */
import { FOUNDATIONS } from '../designFoundations';
import { PALETTE, paletteRgba } from '../designPalette';
/**
 * The rules the extension shipped in blocks/_highlight.css until 2026-10-22.
 * Built on first use, not at import: a bundle that imports the shared ui index
 * but never draws the tooltip (team review's drawer) must tree-shake all of it,
 * and top-level calls would be kept as possible side effects.
 */
function buildCss() {
    const F = FOUNDATIONS;
    const P = PALETTE;
    const v = (token, fallback) => `var(--oq-${token},${fallback})`;
    // The title and the locked state read the brand ramp's step that clears 4.5:1 on this dark card: #3468F8
    // itself measures 3.93:1 on surface.dark, #6090ff 6.08:1 (2026-10-30). Active states use brand, never cyan.
    const BRAND = v('primary-400', P.primary[400]);
    const GREEN = v('green-400', '#4ade80');
    const AMBER = v('amber-400', '#fbbf24');
    const LABEL = v('gray-400', P.neutral[400]);
    const TEXT = v('gray-200', P.neutral[200]);
    const MONO = v('font-family-mono', F.fontFamily.mono);
    return `
.oq-inspector-tooltip{position:absolute;z-index:2147483645;pointer-events:none;box-sizing:border-box;background:${v('sidebar-bg', P.surface.dark)};border-radius:${v('radius-control', F.radius.control)};box-shadow:0 4px 12px ${v('shadow-xl', 'rgba(0,0,0,.3)')};font-family:${v('font-family-system', F.fontFamily.system)};font-size:${v('font-size-compact', F.fontSize.compact)};line-height:normal;color:${TEXT};max-width:320px;min-width:180px;overflow:hidden;text-align:left}
.oq-inspector-tooltip--locked{border:2px solid ${BRAND};box-shadow:0 4px 12px ${v('brand-glow', paletteRgba(P.primary[400], 0.25))}}
.oq-inspector-tooltip__header{display:flex;align-items:center;gap:${F.space[2]};padding:${F.space[2]} ${F.space[3]};border-bottom:1px solid ${v('dark-surface-border', paletteRgba(P.surface.card, 0.1))}}
.oq-inspector-tooltip__icon{flex-shrink:0;width:16px;height:16px;color:${LABEL}}
.oq-inspector-tooltip__icon--lock{color:${BRAND}}
.oq-inspector-tooltip__selector{font-weight:${F.fontWeight.semibold};color:${BRAND};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1}
.oq-inspector-tooltip__body{padding:${F.space[2]} ${F.space[3]}}
.oq-inspector-tooltip__row{display:flex;justify-content:space-between;align-items:center;gap:${F.space[2]};padding:${F.space.half} 0}
.oq-inspector-tooltip__label{color:${LABEL};white-space:nowrap;flex-shrink:0}
.oq-inspector-tooltip__size{flex-shrink:0;white-space:nowrap}
.oq-inspector-tooltip__value{color:${TEXT};font-family:${MONO};font-size:${v('font-size-caption', F.fontSize.caption)}}
.oq-inspector-tooltip__value--token{display:flex;align-items:center;gap:${F.space['1h']}}
.oq-inspector-tooltip__color-swatch{width:12px;height:12px;border-radius:${F.radius.micro};border:1px solid ${v('dark-surface-muted', paletteRgba(P.surface.card, 0.2))};flex-shrink:0}
.oq-inspector-tooltip__token-badge{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border-radius:${F.radius.badge};font-size:${F.fontSize.badge};font-weight:${F.fontWeight.bold};flex-shrink:0;margin-left:${F.space[1]}}
.oq-inspector-tooltip__token-badge--found{background:${v('token-found-bg', 'rgba(74,222,128,.2)')};color:${GREEN}}
.oq-inspector-tooltip__token-badge--missing{background:${v('token-missing-bg', 'rgba(251,191,36,.2)')};color:${AMBER}}
.oq-inspector-tooltip__row--token{justify-content:flex-end;padding:0 0 ${F.space.half} 0}
.oq-inspector-tooltip__token-name,.oq-inspector-tooltip__token-warning{font-family:${MONO};font-size:${F.fontSize.badge};opacity:.85}
.oq-inspector-tooltip__token-name{color:${GREEN}}
.oq-inspector-tooltip__token-warning{color:${AMBER}}
.oq-inspector-tooltip__row--alt{flex-direction:column;align-items:flex-start;gap:${F.space.half}}
.oq-inspector-tooltip__value--alt{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;white-space:normal;max-width:100%}
.oq-inspector-tooltip__value--truncate{max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
`;
}
let cssText;
/** The tooltip's stylesheet text (cached). */
export function inspectorTooltipCss() {
    return (cssText ??= buildCss());
}
const SVG_NS = 'http://www.w3.org/2000/svg';
const HEX = /^#[0-9a-f]{3,8}$/i;
function el(doc, tag, className, text) {
    const node = doc.createElement(tag);
    node.className = className;
    if (text !== undefined)
        node.textContent = text;
    return node;
}
/** Grid glyph while hovering, padlock once the panel is locked. */
function headerIcon(doc, locked) {
    const svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', `oq-inspector-tooltip__icon${locked ? ' oq-inspector-tooltip__icon--lock' : ''}`);
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    const shapes = locked
        ? [['rect', { x: '3', y: '11', width: '18', height: '11', rx: '2' }], ['path', { d: 'M7 11V7a5 5 0 0110 0v4' }]]
        : [['3', '3'], ['14', '3'], ['3', '14'], ['14', '14']].map(([x, y]) => ['rect', { x, y, width: '7', height: '7' }]);
    for (const [tag, attrs] of shapes) {
        const shape = doc.createElementNS(SVG_NS, tag);
        for (const [name, value] of Object.entries({ ...attrs, 'stroke-width': '2' }))
            shape.setAttribute(name, value);
        svg.appendChild(shape);
    }
    return svg;
}
function plainRow(doc, label, value, extra = '') {
    const row = el(doc, 'div', `oq-inspector-tooltip__row${extra === 'alt' ? ' oq-inspector-tooltip__row--alt' : ''}`);
    row.append(el(doc, 'span', 'oq-inspector-tooltip__label', label), el(doc, 'span', `oq-inspector-tooltip__value${extra ? ` oq-inspector-tooltip__value--${extra}` : ''}`, value));
    return row;
}
/** A value with an optional swatch and token badge, plus the token-name row under it. */
function tokenRows(doc, label, value, token, swatch) {
    const shown = HEX.test(value) ? value.toUpperCase() : value;
    const row = el(doc, 'div', 'oq-inspector-tooltip__row');
    const cell = el(doc, 'span', 'oq-inspector-tooltip__value oq-inspector-tooltip__value--token');
    if (swatch && HEX.test(value)) {
        const chip = el(doc, 'span', 'oq-inspector-tooltip__color-swatch');
        chip.style.backgroundColor = value;
        cell.appendChild(chip);
    }
    cell.appendChild(doc.createTextNode(shown));
    if (token !== undefined) {
        cell.appendChild(el(doc, 'span', `oq-inspector-tooltip__token-badge oq-inspector-tooltip__token-badge--${token ? 'found' : 'missing'}`, token ? '✓' : '!'));
    }
    row.append(el(doc, 'span', 'oq-inspector-tooltip__label', label), cell);
    if (token === undefined)
        return [row];
    const tokenRow = el(doc, 'div', 'oq-inspector-tooltip__row oq-inspector-tooltip__row--token');
    tokenRow.appendChild(token
        ? el(doc, 'span', 'oq-inspector-tooltip__token-name', token)
        : el(doc, 'span', 'oq-inspector-tooltip__token-warning', 'No token'));
    return [row, tokenRow];
}
let styleFor;
/** Draw (or redraw) the tooltip inside `container`. The caller positions it. */
export function renderInspectorTooltip(container, data, options = {}) {
    const doc = container.ownerDocument;
    const locked = options.locked === true;
    container.className = `oq-inspector-tooltip${locked ? ' oq-inspector-tooltip--locked' : ''}`;
    styleFor ??= new WeakMap();
    let style = styleFor.get(container);
    if (!style) {
        style = doc.createElement('style');
        style.textContent = inspectorTooltipCss();
        styleFor.set(container, style);
    }
    const header = el(doc, 'div', 'oq-inspector-tooltip__header');
    header.append(headerIcon(doc, locked), el(doc, 'span', 'oq-inspector-tooltip__selector', data.name ?? data.selector), el(doc, 'span', 'oq-inspector-tooltip__value oq-inspector-tooltip__size', `${Math.round(data.dimensions.width)} × ${Math.round(data.dimensions.height)}`));
    const body = el(doc, 'div', 'oq-inspector-tooltip__body');
    const add = (...nodes) => body.append(...nodes);
    if (data.border)
        add(plainRow(doc, 'border', data.border));
    if (data.display)
        add(plainRow(doc, 'display', data.display));
    if (data.margin)
        add(plainRow(doc, 'margin', data.margin));
    if (data.padding)
        add(plainRow(doc, 'padding', data.padding));
    if (data.gap)
        add(plainRow(doc, 'gap', data.gap));
    if (data.borderRadius && data.borderRadius !== '0px')
        add(plainRow(doc, 'border-radius', data.borderRadius));
    if (data.altText)
        add(plainRow(doc, 'alt text', data.altText, 'alt'));
    if (data.fontFamily) {
        add(plainRow(doc, 'font-family', data.fontFamily, 'truncate'));
        if (data.fontSize)
            add(...tokenRows(doc, 'font-size', data.fontSize, data.fontSizeToken, false));
        if (data.fontWeight)
            add(plainRow(doc, 'font-weight', data.fontWeight));
        if (data.lineHeight)
            add(plainRow(doc, 'line-height', data.lineHeight));
    }
    if (data.bgColor)
        add(...tokenRows(doc, 'background', data.bgColor, data.bgColorToken, true));
    if (data.color)
        add(...tokenRows(doc, 'color', data.color, data.colorToken, true));
    if (data.letterSpacing && data.letterSpacing !== 'normal')
        add(plainRow(doc, 'letter-spacing', data.letterSpacing));
    if (data.boxShadow && data.boxShadow !== 'none')
        add(plainRow(doc, 'box-shadow', data.boxShadow, 'truncate'));
    container.replaceChildren(style, header, body);
}
//# sourceMappingURL=inspectorTooltipDom.js.map